import { zodResolver } from "@hookform/resolvers/zod";
import { twoFactorSchema } from "@resume-studio/dto";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@resume-studio/ui";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod/v3";

import { useAdminVerifyOtp } from "../../services/auth/verify-otp";

type FormValues = z.infer<typeof twoFactorSchema>;

export const AdminVerifyOtpPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { verifyOtp, loading } = useAdminVerifyOtp();

  const form = useForm<FormValues>({
    resolver: zodResolver(twoFactorSchema),
    defaultValues: { code: "" },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      await verifyOtp(data);
      const redirect = searchParams.get("redirect");
      navigate(redirect && redirect.startsWith("/") ? redirect : "/", { replace: true });
    } catch {
      form.reset();
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4 py-12">
      <Helmet>
        <title>Two-factor verification · Resume Studio Admin</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="w-full max-w-sm rounded-md border border-border bg-card p-8">
        <h1 className="text-[20px] font-semibold tracking-tight text-foreground">
          Two-factor verification
        </h1>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Enter the 6-digit code from your authenticator app to finish signing in.
        </p>

        <Form {...form}>
          <form className="mt-6 flex flex-col gap-y-4" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              name="code"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-[12px] font-medium text-foreground">
                    One-time password
                  </FormLabel>
                  <FormControl>
                    <Input
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="123456"
                      maxLength={6}
                      className="mono tabular h-10 border-border bg-background text-center text-base tracking-[0.4em] focus-visible:ring-primary"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              disabled={loading}
              className="mt-2 h-9 bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Verify and continue
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
};
